import { ActionCreatorWithoutPayload } from "@reduxjs/toolkit";
import { useCallback, useEffect } from "react";
import { IMessageContainers } from "../../../../MessageComponents/Enums/MessageEnums";
import { getMessageContainerDisplayName } from "../../../../MessageComponents/FunctionMethods/MessageFunctions";
import { MessageContainersToTypes } from "../../../Constants/MessageConstants";
import { ICandidate } from "../../../Typings/Candidate";
import { ICandidateProfile } from "../../../Typings/CandidateProfile";
import {
  addCandidates,
  hideCandidateEmailDialog,
  hideCandidateTextDialog,
  hideDialog,
  hideGroupTabsDialog,
  IMessageDialogState,
  ISelectedCandidate,
  removeCandidatesById,
  resetDialogState,
  selectMessageDialogState,
  selectSelectedCandidatesByType,
  setContainerType,
  setDialogInitialState,
  setDialogTitle,
  setDisableOpen,
  showCandidateEmailDialog,
  showCandidateTextDialog,
  showDialog,
  showGroupTabsDialog,
} from "../../Slices/MessageDialogSlice";
import { useAppDispatch, useAppSelector } from "../reduxHooks";
import { useMessageInputHandlers } from "./useMessageInput";
import { useMessageScheduleHandlers, useMessageScheduleState } from "./useMessageSchedule";
import { useMessageTemplateHandlers } from "./useMessageTemplate";
import { ICandidateWithMessageInfo } from "../../../Typings/MessageText";

export function useMessageDialogState() {
  return useAppSelector(selectMessageDialogState);
}
export function useMessageDialogHandlers() {
  const dispatch = useAppDispatch();
  const { containerType } = useMessageDialogState();
  const selectedEmailCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "email"));
  const selectedTextCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "text"));
  const { showScheduleOptions } = useMessageScheduleState();
  const { handleClearInputState, handleChangeMessageType } = useMessageInputHandlers();
  const { handleClearTemplateInputState } = useMessageTemplateHandlers();
  const { handleClearScheduleOptions, handleChangeMessageType: handleChangeScheduledMessageType } =
    useMessageScheduleHandlers();

  const handleInitializeDialog = (initialState: Partial<IMessageDialogState>) => {
    dispatch(setDialogInitialState(initialState));
  };

  const handleOpenDialog = (initialState?: Partial<IMessageDialogState>) => {
    let dispatchAction = showDialog as ActionCreatorWithoutPayload;
    if (initialState) {
      handleInitializeDialog(initialState);
      handleChangeMessageType(initialState.containerType || IMessageContainers.CandidateTextNow);
      handleChangeScheduledMessageType(
        MessageContainersToTypes[initialState.containerType || IMessageContainers.CandidateTextNow],
      );
      switch (initialState.containerType) {
        case IMessageContainers.CandidateEmailNow:
        case IMessageContainers.CandidateScheduledEmail:
          dispatchAction = showCandidateEmailDialog;
          break;
        case IMessageContainers.CandidateTextNow:
        case IMessageContainers.CandidateScheduledText:
          dispatchAction = showCandidateTextDialog;
          break;
        case IMessageContainers.GroupEmailNow:
        case IMessageContainers.GroupScheduledEmail:
        case IMessageContainers.GroupTextNow:
        case IMessageContainers.GroupScheduledText:
        case IMessageContainers.BulkEmailNow:
        case IMessageContainers.BulkScheduledEmail:
        case IMessageContainers.BulkTextNow:
        case IMessageContainers.BulkScheduledText:
          dispatchAction = showGroupTabsDialog;
          break;
      }
    }
    dispatch(dispatchAction());
  };

  const handleCloseDialog = () => {
    dispatch(hideDialog());
    dispatch(hideCandidateEmailDialog());
    dispatch(hideCandidateTextDialog());
    dispatch(hideGroupTabsDialog());
    handleClearInputState();
    handleClearTemplateInputState();
    handleClearScheduleOptions();
    dispatch(resetDialogState());
  };

  const handleChangeContainerType = (type: IMessageContainers) => {
    dispatch(setContainerType(type));
  };

  const handleDisableDialog = (disabled?: boolean) => {
    dispatch(setDisableOpen(disabled || true));
  };

  const handleAddCandidates = (candidates: ICandidate[] | ICandidateProfile[]) => {
    dispatch(addCandidates(convertToSelectedCandidates(candidates)));
  };

  const handleUpdateDialogTitle = useCallback(() => {
    let displayContainerType = containerType;
    const type = IMessageContainers[containerType].toLowerCase();
    const isEmail = type.includes("email");
    const candidates = isEmail ? selectedEmailCandidates : selectedTextCandidates;
    if (showScheduleOptions && !type.includes("scheduled")) {
      const isGroup = type.includes("group");
      displayContainerType = isEmail
        ? IMessageContainers.BulkScheduledEmail
        : IMessageContainers.BulkScheduledText;
      displayContainerType = isGroup
        ? isEmail
          ? IMessageContainers.GroupScheduledEmail
          : IMessageContainers.GroupScheduledText
        : displayContainerType;
    }
    dispatch(setDialogTitle(getMessageContainerDisplayName(displayContainerType, candidates)));
  }, [containerType, selectedEmailCandidates, selectedTextCandidates, showScheduleOptions]);

  const handleDeselectCandidate = (candidateId: string) => {
    dispatch(removeCandidatesById(candidateId));
  };

  useEffect(() => {
    handleChangeMessageType(containerType);
    handleChangeScheduledMessageType(MessageContainersToTypes[containerType]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerType]);

  useEffect(() => {
    handleUpdateDialogTitle();
  }, [containerType, showScheduleOptions, selectedEmailCandidates, selectedTextCandidates]);

  return {
    handleOpenDialog,
    handleCloseDialog,
    handleChangeContainerType,
    handleDeselectCandidate,
    handleDisableDialog,
    handleAddCandidates,
    handleUpdateDialogTitle,
  } as const;
}

export function toSelectedCandidate(candidate: ICandidate | ICandidateProfile | ICandidateWithMessageInfo) {
  const firstValidPhone = candidate.phoneNumbers.find((p) => p.isValid);
  return {
    candidateId: candidate.id,
    firstName: candidate.firstName,
    lastName: candidate.lastName,
    hasEmail: candidate.emailAddresses.length > 0,
    hasPhone: !!firstValidPhone,
    unsubscribed: candidate.unsubscribed,
    emailAddress: candidate.emailAddresses[0]?.emailAddress,
    phoneNumber: firstValidPhone?.phoneNumber,
  } as ISelectedCandidate;
}

function convertToSelectedCandidates(candidates: ICandidate[] | ICandidateProfile[]): ISelectedCandidate[] {
  return candidates.map(toSelectedCandidate);
}
